<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon">
        <i class="pi pi-file-edit"></i>
      </div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة مقال</h1>
        <p class="form-page-header__subtitle">إضافة مقال جديد للمدونة</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm()">
        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-sitemap"></i></div>
            <span class="form-card__title">التصنيف</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">القسم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="category" :options="categories" showClear optionLabel="title" placeholder="اختر القسم" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">الرابط المختصر (Slug) <span class="optional">اختياري — يُنشأ تلقائياً</span></label>
                  <InputText v-model="slug" placeholder="my-article-title" class="w-100" dir="ltr" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-pencil"></i></div>
            <span class="form-card__title">تفاصيل المقال</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">عنوان المقال <span class="required">*</span></label>
                  <InputText v-model="title" placeholder="أدخل عنوان المقال" class="w-100" />
                  <span class="field-error" v-if="v$.title.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال عنوان المقال
                  </span>
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">ملخص المقال <span class="optional">اختياري — يظهر في قوائم المدونة</span></label>
                  <Textarea v-model="excerpt" rows="2" class="w-100" placeholder="ملخص قصير للمقال..." />
                </div>
              </div>
              <div class="col-12">
                <div class="form-field">
                  <label class="field-label">محتوى المقال <span class="required">*</span></label>
                  <Editor v-model="content" editorStyle="height: 300px" />
                  <span class="field-error" v-if="v$.content.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال محتوى المقال
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-image"></i></div>
            <span class="form-card__title">الصورة المميزة</span>
          </div>
          <div class="form-card__body">
            <div :class="['upload-zone', { 'upload-zone--has-file': featuredImageFile }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${featuredImageFile ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ featuredImageFile ? featuredImageFile.name : 'اضغط لاختيار صورة' }}</p>
              <p class="upload-zone__hint" v-if="!featuredImageFile">PNG, JPG, GIF</p>
              <input type="file" @change="onFeaturedImageChange" accept="image/*" />
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-cog"></i></div>
            <span class="form-card__title">الإعدادات</span>
          </div>
          <div class="form-card__body">
            <div class="source-toggle">
              <i class="pi pi-eye" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">نشر المقال</span>
              <InputSwitch v-model="isPublished" />
              <span class="source-toggle__hint">{{ isPublished ? 'سيظهر المقال في المدونة العامة فوراً' : 'سيُحفظ كمسودة غير ظاهرة' }}</span>
            </div>
            <div class="source-toggle mt-3">
              <i class="pi pi-star-fill" style="color: var(--text-secondary);"></i>
              <span class="source-toggle__label">مقال مميز</span>
              <InputSwitch v-model="isFeatured" />
              <span class="source-toggle__hint">{{ isFeatured ? 'سيظهر ضمن المقالات المميزة' : 'مقال عادي' }}</span>
            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-search"></i></div>
            <span class="form-card__title">تحسين محركات البحث (SEO)</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">عنوان SEO <span class="optional">اختياري — افتراضياً عنوان المقال</span></label>
                  <InputText v-model="metaTitle" class="w-100" />
                </div>
              </div>
              <div class="col-lg-6 col-12">
                <div class="form-field">
                  <label class="field-label">وصف SEO <span class="optional">اختياري — افتراضياً الملخص</span></label>
                  <Textarea v-model="metaDescription" rows="2" class="w-100" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'حفظ المقال' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import Dropdown from 'primevue/dropdown';
import InputSwitch from 'primevue/inputswitch';
import Editor from 'primevue/editor';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useBlogStore } from '@/stores/useBlogStore.js';

export default {
  name: 'BlogAdd',
  components: { Breadcrumb, InputText, Textarea, Dropdown, InputSwitch, Editor },
  data() {
    return {
      v$: useVuelidate(),
      breadcrumbs: [
        { name: 'الرئيسية', path: '/' },
        { name: 'المدونة', path: '/blog' },
        { name: 'إضافة مقال', path: '/blog/add' },
      ],
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: null,
      categories: [],
      featuredImageFile: null,
      isPublished: false,
      isFeatured: false,
      metaTitle: '',
      metaDescription: '',
      formSending: false,
    };
  },
  validations() {
    return {
      title: { required },
      content: { required },
    };
  },
  computed: {
    store() { return useBlogStore(); },
  },
  methods: {
    onFeaturedImageChange(e) {
      this.featuredImageFile = e.target.files[0] || null;
    },
    async fetchCategories() {
      try {
        const { data } = await axios.get('/admin/blog-categories');
        this.categories = data.data ?? [];
      } catch { /* non-fatal */ }
    },
    buildFormData() {
      const fd = new FormData();
      fd.append('title', this.title.trim());
      fd.append('slug', this.slug.trim());
      fd.append('excerpt', this.excerpt ?? '');
      fd.append('content', this.content ?? '');
      if (this.category) fd.append('blog_category_id', this.category.id);
      fd.append('is_published', this.isPublished ? 1 : 0);
      fd.append('is_featured', this.isFeatured ? 1 : 0);
      fd.append('meta_title', this.metaTitle ?? '');
      fd.append('meta_description', this.metaDescription ?? '');
      if (this.featuredImageFile) fd.append('featured_image', this.featuredImageFile);
      return fd;
    },
    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;
      this.formSending = true;
      try {
        await this.store.createPost(this.buildFormData());
        Swal.fire({ icon: 'success', title: 'تم إضافة المقال بنجاح', timer: 1500, showConfirmButton: false });
        this.$router.push('/blog');
      } catch (error) {
        Swal.fire({ icon: 'error', title: error.response?.data?.message || 'حدث خطأ', timer: 2500, showConfirmButton: false });
      } finally {
        this.formSending = false;
      }
    },
  },
  mounted() {
    this.fetchCategories();
  },
};
</script>
